import React, {useCallback, useEffect, useMemo, useRef, useState, useContext, useId} from 'react';

import {
  ENGAGEMENT_DISPLAY_MODE,
  ATTENTION_ANIMATION_DELAY, 
  ATTENTION_ANIMATION_STAGGER,
  ATTENTION_ANIMATION_DURATION,
} from "utils/constants";

import { AttentionAnimation } from 'utils/AttentionAnimationProvider';
import surveyHelpers from 'utils/survey-helper';
import {AUTO_RESIZE_GROUPS} from "utils/AutoResizeProvider";
import {AutoSize} from 'elements/autosize';
import classnames from 'classnames';
import style from './style.module.scss';

import FontAndColorHelper from 'utils/fontAndColorHelper';
import {isMobileDevice} from "utils/environmentHelper";

import {ENGAGEMENT_SLIDE_DURATION} from "compounds/overlay-invideo";

const OPTION_CONTAINER_CLASS = `option-container`

/*
* Text at a manually chosen size, or handed to the auto-sizer when the creator left the size on auto.
* The size itself lives on the parent element, alongside the rest of the font styling, so this only
* decides whether the auto-sizer gets to shrink it to fit.
* */
function SurveyText({manualSize, resizeGroup, isMulti, children}) {
  if (manualSize) {
    return <div className={style.manualSizeText}>{children}</div>;
  }

  return <AutoSize resizeGroup={resizeGroup} isMulti={isMulti}>{children}</AutoSize>;
}

function MultipleChoiceSurveyOption({option, optionFont, optionFontSize, stackOrder, mode, selected, onClick, disabled, noPointer, optionCount,
    submissionsCount, showScreen, selectedOptionForSubmit, showAttentionAnimation, index, onMouseOver, onMouseOut,
    onTouchStart, onTouchEnd, touched, resetKey, delayInitialAttentionAnimation, delayInitialAttentionAnimationForInVideo}) {

  // We need to fill the background color because the optionDecorator can have gaps with the container due to subpixel rendering rounding in various browsers
  const optionStyle = {
    ...((selected && !showScreen) && {borderColor: option?.title?.bgColor, backgroundColor: option?.title?.bgColor}),
    // A manual size can overflow the option, so keep the earlier options above the later ones instead of
    // letting the following option's background paint over the text spilling out of this one.
    ...(optionFontSize && {zIndex: stackOrder}),
  };
  const optionFontSetting = {color: option?.title?.color, font: optionFont, fontSize: optionFontSize};
  const fontAndColor = FontAndColorHelper.getFontAndColor(optionFontSetting);
  const fontWeightAndSize = FontAndColorHelper.getFontWeightAndSize(optionFontSetting);
  const optionId = option?.id;
  const optionTextStyle = {
    color: fontAndColor.color,
    fontFamily: fontAndColor.font,
    ...(fontWeightAndSize && fontWeightAndSize.fontSize && {fontSize: fontWeightAndSize.fontSize}),
    ...(fontWeightAndSize && fontWeightAndSize.fontWeight && {fontWeight: fontWeightAndSize.fontWeight})
  };
  
  const  percentageCount = (optionId) => {
    const displayCount = selectedOptionForSubmit?.id === optionId ?  optionCount + 1  : optionCount;
    const submissionCount = showScreen ? submissionsCount + 1 : submissionsCount;
    return Math.round((100 * displayCount) / submissionCount);
  }

  const optionDecoratorStyle = {
    backgroundColor: option?.title?.bgColor,
    // OV-812 set left 0 to fill option when 100%
    ...(showScreen && {left: 0, width: `${percentageCount(optionId)}%`, opacity: "80%"}),
    ...(showAttentionAnimation && {
      '--vg-fx-attention-duration': `${ATTENTION_ANIMATION_DURATION}s`,
      '--vg-fx-attention-delay': `${(index * ATTENTION_ANIMATION_STAGGER) + (delayInitialAttentionAnimation ? ATTENTION_ANIMATION_DELAY : 0) + (delayInitialAttentionAnimationForInVideo ? ENGAGEMENT_SLIDE_DURATION / 1000 : 0)}s`,
    }),
  }

  // Introduce a single frame delay when we remove attention animation and allowing hover effects 
  // so that the base class attributes can be reapplied, otherwise the initial hover "jumps"
  const [hoverEnabled, setHoverEnabled] = useState(false);
  useEffect(() => {
    if (!showAttentionAnimation) {
      const id = window.requestAnimationFrame(() => {
        setHoverEnabled(true);
      });

      return () => window.cancelAnimationFrame(id);
    }
    setHoverEnabled(false);
  }, [showAttentionAnimation]);

  // Introduce a single frame delay for touched so that we can remove attention on touch and then a frame later add 
  // the base effect style which produces an animation, otherwise there is a "jump" on touch
  const [touchDelayed, setTouchedDelayed] = useState(false);
  useEffect(() => {
    if (touched) {
      const id = window.requestAnimationFrame(() => {
        setTouchedDelayed(true);
      });

      return () => window.cancelAnimationFrame(id);
    }
    setTouchedDelayed(false)
  }, [touched]);

  return(
    <div
      className={classnames(mode !== ENGAGEMENT_DISPLAY_MODE.HORIZONTAL ? style.option : style.optionHorizontal,showScreen ? style.noPointer:null, disabled ? style.optionDisabled : null, {[style.noPointer]: noPointer, [style.optionManualSize]: !!optionFontSize}, OPTION_CONTAINER_CLASS)}
      style={optionStyle}
      onClick={!showScreen ? onClick : null}
      data-testid={`multiple-choice-option-${option.id}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      key={resetKey}
    >
      <div className={classnames(style.optionDecorator, showAttentionAnimation && !touched && style.attentionPulseWidth, touchDelayed && style.optionDecoratorTouched, hoverEnabled && style.optionDecoratorHover)} style={optionDecoratorStyle} />
      <div className={classnames(style.optionText, {[style.optionTextManualSize]: !!optionFontSize})} style={optionTextStyle}>
        {/* TODO: investigate why the non iterative sizing doesn't work in max char cases */}
        <SurveyText manualSize={optionFontSize} resizeGroup={AUTO_RESIZE_GROUPS.MULTIPLE_CHOICE_OPTIONS}>{option?.title?.text}</SurveyText>
      </div>
      {showScreen ? (<div className={classnames(style.optionText,style.optionPercentage, {[style.optionTextManualSize]: !!optionFontSize})} style={optionTextStyle}>
        {/* TODO: investigate why the non iterative sizing doesn't work in max char cases */}
        <SurveyText manualSize={optionFontSize} resizeGroup={AUTO_RESIZE_GROUPS.MULTIPLE_CHOICE_OPTIONS}>{percentageCount(optionId)}%</SurveyText>
      </div>) : <div></div>}
    </div>
  )
}

function MultipleChoiceOptionRenderer(
  {
    option,
    index,
    onClick,
    submitEnabled,
    setSelectedOptionForSubmit,
    onSubmit,
    questionAndOptionsFontSettings,
    optionFontSize,
    stackOrder,
    selectedOptionForSubmit,
    mode,
    surveySubmittingOption,
    setSurveySubmittingOption,
    isCreativeStudioPreviewMode,
    submissionsCount,
    showScreen,
    showAttentionAnimation,
    onMouseOver,
    onMouseOut,
    resetKey,
    delayInitialAttentionAnimation,
    delayInitialAttentionAnimationForInVideo,
    onTouchStart, 
    onTouchEnd, 
    touched,
  }
) {
  const onClickWrapper = useCallback(() => {
    if (isCreativeStudioPreviewMode) {
      return;
    }
    if (submitEnabled) {
      onClick(option.id, index + 1, option.optionIndex);
      setSelectedOptionForSubmit({id: option.id, position: index + 1, optionIndex: option.optionIndex});
    } else {
      if (isMobileDevice()) {
        // animationInProgress is only set when there is animation in progress (ie: when submit button is disabled)
        if (surveySubmittingOption) {
          return;
        }
        // Wait to complete the animation
        setSurveySubmittingOption(option.id);
        setSelectedOptionForSubmit({id: option.id, position: index + 1, optionIndex: option.optionIndex});
        setTimeout(() => {
          onClick(option.id, index + 1, option.optionIndex);
          onSubmit(option.id, index + 1, option.optionIndex);
          setSurveySubmittingOption(null);
        }, 2 * 250); // Sync with transition delay in button.
      } else {
        setSelectedOptionForSubmit({id: option.id, position: index + 1, optionIndex: option.optionIndex});
        onClick(option.id, index + 1, option.optionIndex);
        onSubmit(option.id, index + 1, option.optionIndex);
      }
    }
  }, [submitEnabled, onClick, option, index, setSelectedOptionForSubmit, surveySubmittingOption, setSurveySubmittingOption, onSubmit, isCreativeStudioPreviewMode]);
  const optionCount = option?.responseChoiceCount || 0;
  const selected = useMemo(() => selectedOptionForSubmit && selectedOptionForSubmit.id === option.id, [option, selectedOptionForSubmit]);
  const disabled = useMemo(() => surveySubmittingOption && (surveySubmittingOption !== option.id), [option, surveySubmittingOption]);
  return <MultipleChoiceSurveyOption
    key={option.id}
    option={option}
    optionFont={questionAndOptionsFontSettings.font}
    optionFontSize={optionFontSize}
    stackOrder={stackOrder}
    mode={mode}
    selected={selected}
    onClick={onClickWrapper}
    disabled={disabled}
    noPointer={isCreativeStudioPreviewMode}
    optionCount={optionCount}
    submissionsCount={submissionsCount}
    showScreen={showScreen}
    selectedOptionForSubmit={selectedOptionForSubmit}
    showAttentionAnimation={showAttentionAnimation}
    index={index}
    onMouseOver={onMouseOver}
    onMouseOut={onMouseOut}
    onTouchStart={onTouchStart}
    onTouchEnd={onTouchEnd}
    touched={touched}
    resetKey={resetKey}
    delayInitialAttentionAnimation={delayInitialAttentionAnimation}
    delayInitialAttentionAnimationForInVideo={delayInitialAttentionAnimationForInVideo}
  />
}

function MultipleChoiceSurveyRenderer({title, options, questionAndOptionsFontSettings, optionFontSize, submitEnabled, submitButtonText, submitButtonFontSettings, submitButtonBackgroundColor, mode = ENGAGEMENT_DISPLAY_MODE.VERTICAL, onClick, onSubmit, isNarrow, selectedPosition, isCreativeStudioPreviewMode, isDisplayCreative, pollingEnabled, submissionsCount, showPollingScreen, surveySubmitted, scrubBarShowing,submissionDetails, surveyID, attentionAnimationEnabled, inVideo, ...rest}) {
  const [selectedOptionForSubmit, setSelectedOptionForSubmit] = useState(null);
  const [optionTouched, setOptionTouched] = useState(null);

  const instanceId = useId();

  const {
    attentionHovered, 
    setAttentionHovered, 
    attentionHoveredPreviously, 
    setAttentionHoveredPreviously, 
    attentionResetKey, 
    setAttentionResetKey
  } = useContext(AttentionAnimation);

  useEffect(() => {
    setAttentionHoveredPreviously(false)
  },[attentionAnimationEnabled, setAttentionHoveredPreviously])

  // Clean up attention hover on unmount
  useEffect(() => {
    return () => {
      setAttentionHovered(curr => (curr === instanceId ? null : curr)) 
    };
  }, [setAttentionHovered, instanceId]);
  
  // TODO: test and reconcile various css rules for display, (not) mobile, FourByThree, in-video only
  //      goal is to make one css responsive instead of all these nested conditionals

  useEffect(() => {
    if (selectedPosition !== null && selectedPosition !== undefined) {
      const selectedOption = options[selectedPosition-1]
      setSelectedOptionForSubmit({id: selectedOption.id, position: selectedPosition, optionIndex: selectedOption.optionIndex})
    }
    if(!selectedOptionForSubmit && submissionDetails && submissionDetails[surveyID] && pollingEnabled && !isDisplayCreative) {
      const surveyObj = submissionDetails[surveyID]
      setSelectedOptionForSubmit({id: surveyObj.selectedQuestionOptionID, position: surveyObj.position, optionIndex: surveyObj.optionIndex})
    }
  },[selectedPosition, options, isDisplayCreative , pollingEnabled , submissionDetails, surveyID, ]);

  // Reset css timers for attention animation when option counts change
  const optionsCount = options.length;
  useEffect(() => {
    if (isCreativeStudioPreviewMode) {
      setAttentionResetKey(prevKey => prevKey + 1); 
    }
  }, [optionsCount, setAttentionResetKey, isCreativeStudioPreviewMode]);
  
  const titleFontAndColor = FontAndColorHelper.getFontAndColor(questionAndOptionsFontSettings);
  const titleFontWeightAndSize = FontAndColorHelper.getFontWeightAndSize(questionAndOptionsFontSettings);
  const submitFontAndColor = FontAndColorHelper.getFontAndColor(submitButtonFontSettings);
  const submitFontWeightAndSize = FontAndColorHelper.getFontWeightAndSize(submitButtonFontSettings);

  const [surveySubmittingOption, setSurveySubmittingOption] = useState(null);
  const getVotesStylesClass = (isDisplayCreative) => {
    if (isDisplayCreative || (!scrubBarShowing && mode !== ENGAGEMENT_DISPLAY_MODE.VERTICAL)) {
      return style.votes;
    } else {
      return style.inVideoVotes;
    }
  };

  const submissionTime = submissionDetails && submissionDetails[surveyID]
  const showScreen = isDisplayCreative && surveySubmitted || (!isDisplayCreative && submissionTime) || showPollingScreen;
  const submissionCountDisplay = showScreen ? submissionsCount + 1 : submissionsCount;
  const submissionCountDisplayText = submissionCountDisplay === 1 ? `${submissionCountDisplay} Vote` : `${submissionCountDisplay} Votes`

  const questionFontSize = titleFontWeightAndSize && titleFontWeightAndSize.fontSize;

  const titleStyle = {
    color: titleFontAndColor.color,
    fontFamily: titleFontAndColor.font,
    ...(questionFontSize && {fontSize: questionFontSize}),
    ...(titleFontWeightAndSize && titleFontWeightAndSize.fontWeight && {fontWeight: titleFontWeightAndSize.fontWeight})
  };

  // The vote count borrows the question's font and color but keeps auto-sizing, so it must not pick up
  // the question's manual size.
  const votesStyle = {
    color: titleFontAndColor.color,
    fontFamily: titleFontAndColor.font,
    ...(titleFontWeightAndSize && titleFontWeightAndSize.fontWeight && {fontWeight: titleFontWeightAndSize.fontWeight})
  };

  const submitButtonStyle = {
    backgroundColor: submitButtonBackgroundColor,
    color: submitFontAndColor.color,
    fontFamily: submitFontAndColor.font,
    ...(submitFontWeightAndSize && submitFontWeightAndSize.fontSize && {fontSize: submitFontWeightAndSize.fontSize}),
    ...(submitFontWeightAndSize && submitFontWeightAndSize.fontWeight && {fontWeight: submitFontWeightAndSize.fontWeight})
  }

  const getOptionsContainerClass = () => {
    if(mode === ENGAGEMENT_DISPLAY_MODE.VERTICAL || isNarrow) {
      return style.optionsContainerVertical;
    }
    if(mode === ENGAGEMENT_DISPLAY_MODE.FULL_MODE) {
      return style.optionsContainer;
    }
    if(mode === ENGAGEMENT_DISPLAY_MODE.HORIZONTAL) {
      return style.optionsContainerHorizontal;
    }
    return style.optionsContainer;
  }

  const showAttentionAnimation = attentionAnimationEnabled && !surveySubmittingOption && attentionHovered === null && optionTouched === null;

  const memoizedOptions = useMemo(() => options.map((option, index) =>
    <MultipleChoiceOptionRenderer
      key={option.id}
      option={option}
      index={index}
      onClick={onClick}
      submitEnabled={submitEnabled}
      setSelectedOptionForSubmit={setSelectedOptionForSubmit}
      onSubmit={onSubmit}
      questionAndOptionsFontSettings={questionAndOptionsFontSettings}
      optionFontSize={optionFontSize}
      stackOrder={options.length - index}
      selectedOptionForSubmit={selectedOptionForSubmit}
      mode={mode}
      setSurveySubmittingOption={setSurveySubmittingOption}
      surveySubmittingOption={surveySubmittingOption}
      isCreativeStudioPreviewMode={isCreativeStudioPreviewMode}
      submissionsCount={submissionsCount}
      showScreen={showScreen}
      showAttentionAnimation={showAttentionAnimation}
      onMouseOver={() => {
        setAttentionHovered(instanceId); 
        setAttentionHoveredPreviously(true);
        // this handles clearing touch selection for desktops with touchscreens when switching from touch to mouse events
        if (!isMobileDevice()) {
          setOptionTouched(null);
        }
      }}
      onMouseOut={() => setAttentionHovered(null)}
      onTouchStart={() => setOptionTouched(index)}
      onTouchEnd={() => setOptionTouched(null)}
      touched={optionTouched === index}
      resetKey={attentionResetKey}
      delayInitialAttentionAnimation={attentionHoveredPreviously}
      delayInitialAttentionAnimationForInVideo={inVideo}
    />
  ), [mode, onClick, onSubmit, options, questionAndOptionsFontSettings, optionFontSize, selectedOptionForSubmit,
    submitEnabled, surveySubmittingOption, isCreativeStudioPreviewMode, submissionsCount, showScreen, 
    showAttentionAnimation, attentionResetKey, attentionHoveredPreviously, inVideo, optionTouched,
    setAttentionHovered, setAttentionHoveredPreviously, instanceId
  ]);

  return (
    <div className={mode !== ENGAGEMENT_DISPLAY_MODE.HORIZONTAL ? style.multipleChoiceContainer : style.multipleChoiceHorizontalContainer}>
      <div className={classnames(mode !== ENGAGEMENT_DISPLAY_MODE.HORIZONTAL ? style.title : style.titleHorizontal, {[style.titleManualSize]: !!questionFontSize})} style={titleStyle}>
        <SurveyText manualSize={questionFontSize} isMulti={true}>{title}</SurveyText>
      </div>
      <div className={getOptionsContainerClass()}>
        {memoizedOptions}
      </div>
      {submitEnabled && mode !== ENGAGEMENT_DISPLAY_MODE.HORIZONTAL && !showScreen &&
        <div
          className={classnames(style.submitButton, {[style.noPointer]: isCreativeStudioPreviewMode})}
          style={submitButtonStyle}
          onClick={() => {
            if (isCreativeStudioPreviewMode) {
              return 
            }

            if (!selectedOptionForSubmit) {
              surveyHelpers.showNoOptionSelectedAlert();
            } else {
              onSubmit(selectedOptionForSubmit.id, selectedOptionForSubmit.position, selectedOptionForSubmit.optionIndex);
            }
          }}
          data-testid={'multiple-choice-submit'}
        >
          <AutoSize isMulti={true} resizeGroup={AUTO_RESIZE_GROUPS.MULTIPLE_CHOICE_OPTIONS}>{submitButtonText}</AutoSize>
        </div>
      }
        {pollingEnabled && (
        <div className={getVotesStylesClass(isDisplayCreative)} style={votesStyle}>
          {/* replace the vote count from backend api */}
          <AutoSize isMulti={true} resizeGroup={AUTO_RESIZE_GROUPS.MULTIPLE_CHOICE_OPTIONS}>{submissionCountDisplayText}</AutoSize>
        </div>
      )}
    </div>
  );
}

export default MultipleChoiceSurveyRenderer;
